import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Clock, PackageCheck, Route, Store, Wallet } from 'lucide-react';

import type { DeliveryJob } from '@/api/types';
import { formatElapsed, formatMoney } from '@/lib/format';
import { formatLegDistance, jobLeg } from '@/lib/route';
import {
  isCashOnDelivery,
  isInHand,
  isReadyToCollect,
  JOB_STEPS,
  jobStep,
  nextStep,
} from '@/lib/status';
import { cn } from '@/lib/utils';
import { StatusPill } from './status-pill';
import { Badge } from './ui/badge';
import { Card } from './ui/card';

/**
 * One job on the run sheet.
 *
 * `lead` is the job at the top — the one already in the bag, or the next to be
 * picked up. It is drawn larger and on a raised ground because a rider carries
 * ONE bag: everything below it is queued, and four identical cards would give
 * the eye nowhere to land at the moment they most need somewhere.
 *
 * Every card ends in the same place: a plain "Next:" instruction. Someone who
 * is not at ease with the app should be able to work a whole shift by reading
 * only that line and tapping the card.
 */
export function JobCard({
  job,
  lead = false,
  stopNumber,
  hideShop = false,
}: {
  job: DeliveryJob;
  lead?: boolean;
  /** Its place in the suggested drop order, drawn as a large numeral. */
  stopNumber?: number;
  /** Inside a shop group the shop is already named in the group's heading. */
  hideShop?: boolean;
}) {
  const cod = isCashOnDelivery(job);
  const carrying = isInHand(job.status);
  const trip = formatLegDistance(jobLeg(job));

  return (
    <Card
      asChild
      variant={lead ? 'raised' : 'default'}
      interactive
      className={cn(lead && 'border-line-brand/40')}
    >
      <Link
        to={`/jobs/${job.id}`}
        className={cn(
          'block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          lead ? 'p-5' : 'p-4',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {stopNumber !== undefined ? (
              <span
                data-numeric
                className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-on-primary"
              >
                <span className="sr-only-text">Stop </span>
                {stopNumber}
              </span>
            ) : null}
            <StatusPill status={job.status} size={lead ? 'md' : 'sm'} />
          </div>
          <span
            data-numeric
            className="flex shrink-0 items-center gap-1 text-xs text-ink-subtle"
            title="How long this job has been open"
          >
            <Clock className="size-3.5" aria-hidden="true" />
            {formatElapsed(job.createdAt)}
          </span>
        </div>

        <p
          className={cn(
            'mt-3 font-bold tracking-tight text-ink [overflow-wrap:anywhere]',
            lead ? 'text-2xl' : 'text-lg',
          )}
        >
          {job.orderNumber ?? 'Order'}
        </p>

        <dl className="mt-2 grid gap-1 text-[0.9375rem] leading-snug">
          {hideShop ? null : (
            <div className="flex items-start gap-2">
              <dt className="sr-only-text">Collect from</dt>
              <Store className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
              <dd className="min-w-0 text-ink-muted [overflow-wrap:anywhere]">
                {job.pharmacyName ?? 'Pharmacy'}
              </dd>
            </div>
          )}
          {job.customerName ? (
            <div className="flex items-start gap-2">
              <dt className="sr-only-text">Deliver to</dt>
              <PackageCheck className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
              <dd className="min-w-0 text-ink-muted [overflow-wrap:anywhere]">
                {job.customerName}
              </dd>
            </div>
          ) : null}
        </dl>

        {/*
         * The facts that change what the rider does next, and nothing else.
         * "Packed and waiting" is why they set off; the cash figure is what
         * they must not forget to ask for; the trip is how long it will take.
         */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {cod ? (
            <Badge tone="danger" icon={Wallet}>
              <span data-numeric>Collect {formatMoney(job.codAmount)}</span>
            </Badge>
          ) : null}
          {!carrying && isReadyToCollect(job) ? (
            <Badge tone="success" icon={PackageCheck}>
              Packed and waiting
            </Badge>
          ) : null}
          {trip ? (
            <Badge tone="neutral" icon={Route}>
              <span data-numeric>About {trip}</span>
            </Badge>
          ) : null}
        </div>

        <StepMeter job={job} />

        <p
          className={cn(
            'mt-3 flex items-center gap-2 border-t border-line pt-3 font-semibold text-brand',
            lead ? 'text-base' : 'text-[0.9375rem]',
          )}
        >
          <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="text-ink-muted">Next: </span>
            {nextStep(job)}
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-subtle" aria-hidden="true" />
        </p>
      </Link>
    </Card>
  );
}

/**
 * "Step 2 of 4 · Go to shop", with four segments under it.
 *
 * The words carry it; the bar is a glance for anyone who does not read the
 * words, and is hidden from screen readers so the step is not said twice.
 */
function StepMeter({ job }: { job: DeliveryJob }) {
  const step = jobStep(job.status);
  if (step >= JOB_STEPS.length) return null;

  return (
    <div className="mt-3 grid gap-1.5">
      <p data-numeric className="text-xs font-medium text-ink-subtle">
        Step {step + 1} of {JOB_STEPS.length} · {JOB_STEPS[step]}
      </p>
      <div className="grid grid-cols-4 gap-1" aria-hidden="true">
        {JOB_STEPS.map((label, index) => (
          <span
            key={label}
            className={cn(
              'h-1.5 rounded-full',
              index < step && 'bg-primary',
              index === step && 'bg-primary/55',
              index > step && 'bg-muted',
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function JobCardSkeleton({ lead = false }: { lead?: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse rounded-xl border border-line bg-raised motion-reduce:animate-none',
        lead ? 'h-56 p-5' : 'h-44 p-4',
      )}
    />
  );
}
